// src/components/Lightbox.jsx
//
// Fullscreen overlay shown when a panel is tapped or clicked.
// - Black backdrop, image centred
// - Click backdrop, click ✕, or press Escape to close
// - Browser-native pinch-zoom works inside the overlay on touch
// - Locks page scroll while open, and returns focus on close

import React, { useEffect, useRef } from "react";
import useEscapeKey from "../hooks/useEscapeKey";

export default function Lightbox({ src, alt, onClose }) {
  const closeButtonRef = useRef(null);
  const previouslyFocused = useRef(null);

  // Escape + scroll lock, shared with BidModal.
  useEscapeKey(onClose, Boolean(src), true);

  useEffect(() => {
    if (!src) return undefined;

    // Remember where focus was so it can be restored on close. Without this,
    // closing the lightbox drops focus to <body> and a keyboard user has to
    // tab from the top of the page to get back to the panel they were reading.
    previouslyFocused.current = document.activeElement;
    closeButtonRef.current?.focus();

    return () => {
      previouslyFocused.current?.focus?.();
    };
  }, [src]);

  if (!src) return null;

  return (
    <div
      className="lightbox-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
    >
      <button
        ref={closeButtonRef}
        type="button"
        className="lightbox-close"
        onClick={onClose}
        aria-label="Close image viewer"
        onMouseDown={(e) => e.stopPropagation()}
      >
        ✕
      </button>

      <img
        src={src}
        alt={alt || ""}
        className="lightbox-image"
        onClick={(e) => e.stopPropagation()}
        draggable={false}
      />

      <p className="lightbox-hint" aria-hidden="true">
        Pinch to zoom · tap outside to close
      </p>
    </div>
  );
}